import React from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

const sampleField = {
  label: "Purchase Price",
  attributeName: "purchasePrice",
  type: "currency",
  placeholder: "Enter amount",
  required: true,
  readOnly: false,
  metadata: {
    format: "currency",
  },
};

const guidelines = [
  "Labels are human-readable, Title Case when possible.",
  "attributeName preserves author casing (trimmed) and must be unique across fields and sections (case-insensitive).",
  "Fields must define a valid type (input, textarea, currency, datetime, checkbox, radio, decision, inlineInput).",
  "Validation is explicit; required fields must set validation.required = true.",
  "Set validationErrorMessage when validation is required to provide consistent UX.",
  "Metadata is structured and type-specific (e.g., currency format, datetime format).",
  "Option-based fields must include metadata.options and, for *WithOther, metadata.other.input with {value}Input naming.",
  'For percentage inputs (often placeholder "%"), templates use metadata.format: "percentage" and a percentage-specific validationErrorMessage (see Seller_s_Estimate_Net_Sheet).',
  'For percentage inputs, validation.patterns is often a regex like "[0-9]{1,2}[.]{1}[0-9]{1,3}%".',
  'For currency inputs (often placeholder "$"), templates use metadata.format: "currency" and may include validation.patterns like "[0-9.,$]{1,15}".',
  "Use metadata.clearValue to define a mutually-exclusive option such as None.",
  "Decision/decisionWithOther options may include goTo to route to a section.",
  "Datetime fields use metadata.fieldData.format and may include relative day helpers like mec, mecIsCoe, mecIsAbs, mecIsDbc (days before closing), and notSetValue.",
  "Multi-part dates (day, month, year as separate inputs): use attributeName_MM, _DD, _YY or _YYYY, _HH, _mm (minutes), _TEXT (e.g. closing_date_dd, closing_date_mm, closing_date_yy). Annotator places the boxes; JSON role sets attribute names and optional day_eval, month_eval, year_eval for backend interpretation (numeric month, month name, etc.).",
  "InlineInput fields store their input definition in metadata.content[0] (attributeName, labels, validation, width).",
  "Use constraint and constraintId for conditional logic; keep shapes consistent across fields.",
  "Use readOnly for prefilled values and customTemplateDoNotCopy for template-only fields.",
  "Content fields store HTML in value; keep markup minimal and consistent.",
  "Form templates must define category, meviDealRoleCreator, formData, formSummaryJSON, dealSummaryJSON, signaturesJSON, and rulesJSON.",
  "formSummaryJSON and dealSummaryJSON map attributeName to {label, type, display}.",
  "Form uploads use metadata.formUploadData (and optional formUploadData2) arrays plus metadata.formUploadSettings titles/descriptions.",
  "Offer compare uses metadata.offerCompareData at the form level; most templates leave it empty until fields are tagged.",
  "Sections may define metadata.meviDealRoleRules for read/write access and optional goTo routing.",
];

const designPatterns = [
  "Keep attributeName stable across versions; prefer label changes over renames.",
  "Option values are durable identifiers—avoid changing values after release.",
  "Decision fields should include goTo on each option for predictable routing.",
  "Use metadata.format to indicate specialized inputs (currency, percentage, datetime).",
  "Use metadata.labelType to control option layout (stacked vs inline).",
  "For decisionWithOther, pair metadata.other.decisionValue with metadata.other.input.",
  "Use metadata.fieldData.inputCssClass to align other inputs with textarea styling.",
  "Prefer constraint/constraintId over custom metadata for conditional rules.",
];

const considerations = [
  "Changing field types, removing fields, or renaming attributeName may require migration.",
  "Renaming option values can break existing data mappings.",
  "Use validationErrorMessage for required inputs to avoid empty errors.",
  "Keep placeholders concise and optional; rely on labels for meaning.",
  "Apply section access rules consistently for client/team roles.",
  "Form metadata (dataExtraction, formUploadData, formUploadSettings, daysAfter) must stay aligned with downstream pipelines.",
  "Versioning drives which HTML directory is used; keep versions accurate to avoid wrong assets.",
];

const FieldGuidelines = () => {
  const sections = [
    { id: "overview", label: "Overview" },
    { id: "standards", label: "Standards" },
    { id: "design-patterns", label: "Design Patterns" },
    { id: "considerations", label: "Considerations" },
    { id: "schema", label: "Field Schema" },
    { id: "with-other", label: "*WithOther" },
    { id: "options", label: "Options" },
    { id: "inline-input", label: "Inline Input" },
    { id: "content-fields", label: "Content Fields" },
    { id: "mec-metadata", label: "MEC Metadata" },
    { id: "date-field-naming", label: "Date Field Naming" },
    { id: "section-access", label: "Section Access" },
    { id: "template-structure", label: "Template Structure" },
    { id: "template-metadata", label: "Template Metadata" },
    { id: "integrations", label: "Integrations" },
    { id: "versioning", label: "Versioning" },
    { id: "folder-convention", label: "Folder Convention" },
  ];

  return (
    <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Quick Links
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {sections.map((section) => (
              <Typography
                key={section.id}
                component="a"
                href={`#${section.id}`}
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {section.label}
              </Typography>
            ))}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          <Box id="overview">
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Field Guidelines
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use these standards for consistent field creation. These rules
                  are enforced in the Form Studio and used to validate metadata
                  integrity. Perform a JSON Audit to ensure your fields are
                  compliant.
                </Typography>
              </Box>
              <Button variant="contained" color="primary">
                JSON Audit
              </Button>
            </Box>
          </Box>

          <Divider />

          <Box id="standards">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Standards and Requirements
            </Typography>
            <Stack spacing={1}>
              {guidelines.map((rule) => (
                <Typography key={rule} variant="body2">
                  • {rule}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box id="design-patterns">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Design Patterns
            </Typography>
            <Stack spacing={1}>
              {designPatterns.map((rule) => (
                <Typography key={rule} variant="body2">
                  • {rule}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box id="considerations">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Considerations
            </Typography>
            <Stack spacing={1}>
              {considerations.map((rule) => (
                <Typography key={rule} variant="body2">
                  • {rule}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box id="schema">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Field Schema (Core Properties)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These properties appear consistently across templates and should
              be supplied unless a field type specifically omits them.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "35%" }}>Core fields</TableCell>
                        <TableCell>
                          order, type, constraint, constraintId, placeholder,
                          info, hint, label, attributeName, eventTrigger, value,
                          validation, validationErrorMessage, metadata,
                          readOnly, customTemplateDoNotCopy
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Validation shape</TableCell>
                        <TableCell>
                          min, max, minLength, maxLength, patterns, required
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack spacing={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      input
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "input",
  "attributeName": "buyerNames",
  "label": "Buyers",
  "placeholder": "Full legal names",
  "validation": {
    "required": true
  },
  "validationErrorMessage": "Required"
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      textarea
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "textarea",
  "attributeName": "legalDescription",
  "label": "Legal Description",
  "placeholder": "lot and block",
  "validation": {
    "required": true
  },
  "validationErrorMessage": "Required"
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      currency (input + metadata.format)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "input",
  "attributeName": "purchasePrice",
  "label": "Purchase Price",
  "placeholder": "$",
  "validation": {
    "patterns": [
      "[0-9.,$]{1,15}"
    ],
    "required": true
  },
  "validationErrorMessage": "Required",
  "metadata": {
    "format": "currency"
  }
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      percentage (input + metadata.format)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "input",
  "attributeName": "interestRate",
  "label": "Interest %",
  "placeholder": "%",
  "validation": {
    "patterns": [
      "[0-9]{1,2}[.]{1}[0-9]{1,3}%"
    ],
    "required": true
  },
  "validationErrorMessage": "Required",
  "metadata": {
    "format": "percentage"
  }
}`}
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="with-other">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              *WithOther Naming Convention
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For checkboxWithOther and radioWithOther fields, the free-text
              input must follow a strict naming rule so downstream systems can
              reliably map values.
            </Typography>

            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Rule
                    </Typography>
                    <Typography variant="body2">
                      If the option value is <code>other</code>, the input
                      attribute must be <code>otherInput</code>.
                    </Typography>
                    <Typography variant="body2">
                      Formula: <code>{"{value}Input"}</code>
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      checkboxWithOther
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "checkboxWithOther",
  "attributeName": "buyerTerminationConditions",
  "metadata": {
    "options": [
      {
        "value": "termInspection",
        "text": "Inspection"
      },
      {
        "value": "termInsurance",
        "text": "Causes of Loss, Insurance"
      }
    ],
    "other": {
      "checkbox": {
        "text": "Other",
        "value": "termOther"
      },
      "input": {
        "attributeName": "termOtherInput",
        "placeholder": "details...",
        "validation": {
          "required": true
        }
      }
    }
  },
  "fieldData": {
    "inputCssClass": "data-textarea"
  }
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      radioWithOther
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "radioWithOther",
  "attributeName": "financingType",
  "label": "Financing Type",
  "metadata": {
    "labelType": "stacked",
    "options": [
      {
        "value": "fha",
        "text": "FHA"
      },
      {
        "value": "conventional",
        "text": "Conventional"
      }
    ],
    "other": {
      "radio": {
        "text": "Other",
        "value": "financingOther"
      },
      "input": {
        "attributeName": "financingOtherInput",
        "placeholder": "details..."
      }
    }
  }
}`}
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="options">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Option Fields (metadata.options)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Option fields must provide a list of value/text pairs.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "45%" }}>
                          metadata.options[0]
                        </TableCell>
                        <TableCell>
                          {'{ value: "availableFundsDoes", text: "Does" }'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.options[1]</TableCell>
                        <TableCell>
                          {
                            '{ value: "availableFundsDoesNot", text: "Does Not" }'
                          }
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      checkbox (options + clearValue)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "checkbox",
  "attributeName": "availableFunds",
  "label": "Available Funds",
  "metadata": {
    "options": [
      {
        "value": "availableFundsDoes",
        "text": "Does"
      },
      {
        "value": "availableFundsDoesNot",
        "text": "Does Not"
      }
    ],
    "clearValue": "availableFundsDoesNot"
  }
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      decision (options + goTo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "decision",
  "attributeName": "acceptOffer",
  "label": "Accept Offer?",
  "metadata": {
    "options": [
      {
        "value": "yes",
        "text": "Yes",
        "goTo": "nextSection"
      },
      {
        "value": "no",
        "text": "No",
        "goTo": "endSection"
      }
    ]
  }
}`}
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      decisionWithOther
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {`{
  "type": "decisionWithOther",
  "attributeName": "loanType",
  "label": "Financing Type",
  "metadata": {
    "options": [
      {
        "value": "convLoan",
        "text": "Conventional",
        "goTo": "next"
      },
      {
        "value": "fhaLoan",
        "text": "FHA",
        "goTo": "next"
      }
    ],
    "other": {
      "decisionValue": "otherLoan",
      "input": {
        "type": "input",
        "attributeName": "otherLoanType",
        "validation": {
          "required": true
        },
        "validationErrorMessage": "Required"
      }
    }
  }
}`}
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="inline-input">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Inline Input Fields (metadata.content)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              InlineInput stores its input definition inside
              metadata.content[0].
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "45%" }}>
                          metadata.content[0].attributeName
                        </TableCell>
                        <TableCell>county</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.content[0].preLabel</TableCell>
                        <TableCell>Text before the input</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.content[0].postLabel</TableCell>
                        <TableCell>Text after the input</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          metadata.content[0].validation.required
                        </TableCell>
                        <TableCell>true</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.content[0].width</TableCell>
                        <TableCell>150px</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    inlineInput
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "type": "inlineInput",
  "attributeName": "propertyCounty",
  "metadata": {
    "content": [
      {
        "attributeName": "county",
        "preLabel": "The Property is ... County of ",
        "postLabel": ", Colorado:",
        "validation": {
          "required": true
        },
        "validationErrorMessage": "County is required",
        "width": "150px"
      }
    ]
  }
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="content-fields">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Content Fields (HTML)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Content fields inject HTML into the rendered form. Keep markup
              small, semantic, and consistent with CSS conventions.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>type</TableCell>
                        <TableCell>content</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>attributeName</TableCell>
                        <TableCell>Unique, descriptive identifier</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>value</TableCell>
                        <TableCell>HTML string to render</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    content
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "type": "content",
  "attributeName": "additionalProvisionsContent",
  "value": "<span class='contentField'><strong>31.1.1.</strong> Post-Closing Occupancy Agreement.</span>"
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="mec-metadata">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              MEC Metadata (Field-Level)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Datetime fields can define relative offsets tied to
              MEC/BS/BAD/COE settings. These values are stored on the field
              metadata and read against form-level daysAfter defaults.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>
                          metadata.mec
                        </TableCell>
                        <TableCell>
                          Offset in days from MEC (e.g., 0, -1, 5)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.mecOnly</TableCell>
                        <TableCell>
                          true = disable the date picker; only a MEC number
                          may be entered (e.g., tiny single-digit PDF boxes)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.mecIsCoe</TableCell>
                        <TableCell>
                          Use COE as the reference date for mec offsets
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.mecIsAbs</TableCell>
                        <TableCell>
                          Use ABS as the reference date for mec offsets
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.mecIsDbc</TableCell>
                        <TableCell>
                          Days before closing: count the mec offset backwards
                          from the DBC anchor date (independent of COE)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.notSetValue</TableCell>
                        <TableCell>
                          Default value when base date is not set
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    datetime (MEC offsets)
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "type": "datetime",
  "attributeName": "closingDate",
  "validation": {
    "required": true
  },
  "metadata": {
    "fieldData": {
      "format": "01/02/2006"
    },
    "mec": 0,
    "mecOnly": true,
    "mecIsCoe": true
  }
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="date-field-naming">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Date Field Naming (Multi-Part Dates)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For forms that split date into day, month, and year (e.g. closing or acceptance date), use a consistent attribute naming convention and optional eval flags for the backend.
            </Typography>
            <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper", mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Attribute name suffixes
              </Typography>
              <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                Use underscore plus suffix on the base attribute name:
              </Typography>
              <Stack component="ul" sx={{ pl: 2.5, m: 0 }}>
                <Typography component="li" variant="body2">MM — month (numeric or text)</Typography>
                <Typography component="li" variant="body2">DD — day of month</Typography>
                <Typography component="li" variant="body2">YY or YYYY — year (2- or 4-digit)</Typography>
                <Typography component="li" variant="body2">HH — hour</Typography>
                <Typography component="li" variant="body2">mm — minutes</Typography>
                <Typography component="li" variant="body2">TEXT — plain text (e.g. month name like &quot;May&quot;)</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Examples: closing_date_dd, closing_date_mm, closing_date_yy (or closing_date_yyyy).
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Eval flags (backend)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                When day_eval, month_eval, or year_eval are present, the engine can interpret numeric month (1–12), month name or abbreviation (&quot;January&quot;, &quot;Jan&quot;), etc. Set these in JSON mode as needed; Annotator does not configure eval flags.
              </Typography>
            </Paper>
          </Box>

          <Divider />

          <Box id="section-access">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Section Access Rules (metadata.meviDealRoleRules)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sections can restrict read/write access with role rules.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "35%" }}>
                          metadata.meviDealRoleRules.write
                        </TableCell>
                        <TableCell>["team.buy"]</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.meviDealRoleRules.read</TableCell>
                        <TableCell>
                          ["team.sell", "client.buy", "client.sell"]
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>section.goTo (optional)</TableCell>
                        <TableCell>sellers</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    section rules
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "attributeName": "buyers",
  "metadata": {
    "meviDealRoleRules": {
      "write": [
        "team.buy"
      ],
      "read": [
        "team.sell",
        "client.buy",
        "client.sell"
      ]
    }
  },
  "goTo": "sellers"
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="template-structure">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Template Structure (Form-Level)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Form templates define global settings, field data, summaries, and
              signing rules. These top-level objects are required for consistent
              behavior across the platform.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>id</TableCell>
                        <TableCell>Unique form identifier</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>category</TableCell>
                        <TableCell>
                          salesContract, clientRelationshipAgreements, notices,
                          disclosures, brokerageForm, closing, addendaToContract
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.meviDealRoleCreator</TableCell>
                        <TableCell>
                          ["team.buy"], ["team.sell"], or "other.closingOrAdmin"
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>formData</TableCell>
                        <TableCell>
                          Sections and fields (the core template data)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>formSummaryJSON</TableCell>
                        <TableCell>
                          Map of attributeName to {"{label, type, display}"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>dealSummaryJSON</TableCell>
                        <TableCell>
                          Map of attributeName to {"{label, type, display}"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>signaturesJSON</TableCell>
                        <TableCell>
                          Signing roles and signature blocks
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>rulesJSON</TableCell>
                        <TableCell>
                          getDesignatedSigners and setDesignatedSigners for
                          signer routing
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    template skeleton
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "id": "uuid",
  "metadata": {
    "category": "salesContract",
    "meviDealRoleCreator": [
      "team.buy"
    ]
  },
  "formJSON": {
    "formData": [
      {
        "sections": []
      }
    ]
  },
  "formSummaryJSON": {
    "purchasePrice": {
      "label": "Purchase Price",
      "type": "currency",
      "display": true
    }
  },
  "dealSummaryJSON": {
    "purchasePrice": {
      "label": "Purchase Price",
      "type": "currency",
      "display": true
    }
  },
  "signaturesJSON": {},
  "rulesJSON": {}
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="template-metadata">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Template Metadata (Form-Level)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Form templates can include metadata that powers extraction,
              uploads, and relative date rules. Keep these structures consistent
              with the backend pipelines.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Milestone/timeline offsets live on datetime fields as metadata.mec
              with optional mecIsCoe, mecIsAbs, and notSetValue.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>
                          metadata.dataExtraction.sensible
                        </TableCell>
                        <TableCell>
                          documentType, configName, configVersion, demoFilename,
                          demoExtractionId
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.formUploadData</TableCell>
                        <TableCell>
                          ["purchasePrice", "closingDate", "loanType", …]
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.formUploadData2</TableCell>
                        <TableCell>Secondary upload group (optional)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.formUploadSettings</TableCell>
                        <TableCell>
                          Titles/descriptions keyed by formUploadData1/2
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.offerCompareData</TableCell>
                        <TableCell>
                          Array of attributeName used for offer compare (often
                          empty)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>metadata.settings.daysAfter</TableCell>
                        <TableCell>
                          notSetValue + mec/abs/bad/coe labels, info,
                          preposition, absFormUpload
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    form metadata
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "metadata": {
    "dataExtraction": {
      "sensible": {
        "documentType": "ca_purchasecontract",
        "configName": "residentialpurchaseagreement",
        "configVersion": "hash",
        "demoFilename": "California_Purchase_Contract_Example_1",
        "demoExtractionId": "uuid"
      }
    },
    "formUploadData": [
      "purchasePrice",
      "closingDate",
      "loanType"
    ],
    "formUploadData2": [
      "titleReportDeadline",
      "homeWarrantyAllowance"
    ],
    "formUploadSettings": {
      "formUploadData1": {
        "title": "Purchase Contract Data",
        "description": "Optional fields that power offer comparison and timelines."
      },
      "formUploadData2": {
        "title": "Additional Dates",
        "description": "Optional fields that power timelines."
      }
    },
    "offerCompareData": [],
    "settings": {
      "daysAfter": {
        "notSetValue": 0,
        "mecLabel": "MEC",
        "mecInfo": "Number of days from Mutually Executed Contract",
        "mecPreposition": "after",
        "absLabel": "ABS",
        "absInfo": "Number of days after Buyer Signatures",
        "absPreposition": "after",
        "absFormUpload": true,
        "badLabel": "BAD",
        "badInfo": "Number of days after Binding Agreement Date",
        "badPreposition": "after",
        "coeLabel": "COE",
        "coeInfo": "Number of days after Close of Escrow",
        "coePreposition": "after"
      }
    }
  }
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="integrations">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Integrations.json Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Integration entries bind a form template to its HTML/CSS assets
              and interactive signing configuration.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>
                          formTemplateId
                        </TableCell>
                        <TableCell>Matches the form template id</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>locale</TableCell>
                        <TableCell>
                          State or brokerage locale used for assets
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>cssFileId</TableCell>
                        <TableCell>CSS asset id used for styling</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>htmlTemplateId</TableCell>
                        <TableCell>HTML file used to build the form</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>interactiveForms</TableCell>
                        <TableCell>
                          Signing ceremony configuration (per locale spec)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>footerHtmlId</TableCell>
                        <TableCell>HTML file used for the footer</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    integration entry
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "formTemplateId": "uuid",
  "locale": "CO",
  "cssFileId": "co-contract.css",
  "htmlTemplateId": "contract.html",
  "interactiveForms": [
    "signing"
  ],
  "footerHtmlId": "footer.html"
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="versioning">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Form Versioning (Semantic)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Versions follow Major.Minor.Revision and drive which HTML assets
              are selected by the API.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>Major</TableCell>
                        <TableCell>
                          Year of form set; opt-in migration for existing
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Minor</TableCell>
                        <TableCell>
                          Minor asset changes; opt-in migration for existing
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Revision</TableCell>
                        <TableCell>Patch updates; auto-upgraded</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    version example
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "version": "21.2.3"
}`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box id="folder-convention">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Folder Convention (HTML/CSS)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              HTML files live under files/templates/form/html/{`<locale>`}/ and
              are versioned by year and minor updates.
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: "40%" }}>Base path</TableCell>
                        <TableCell>
                          files/templates/form/html/{`<locale>`}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Year directory</TableCell>
                        <TableCell>
                          CO/21 for base CO forms; CO/22 for 2022 updates
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Minor updates</TableCell>
                        <TableCell>
                          Nested folders like CO/22/2 for mid-year updates
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Asset requirement</TableCell>
                        <TableCell>
                          Each directory includes its own css, headers, footers
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Version routing</TableCell>
                        <TableCell>
                          Version 21.2.3 maps to CO/21/2/&lt;form.html&gt;
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    asset path
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`files/templates/form/html/CO/21/2/contract.html`}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default FieldGuidelines;
